import React, { useState } from 'react';
import Header from '../../components/Header/Header';
import GuestDetailsForm from '../../components/GuestDetailsForm/GuestDetailsForm';
import BookingSummary from '../../components/BookingSummary/BookingSummary';
import Button from '../../components/Button/Button';
import Footer from '../../components/Footer/Footer';
import './Checkout.css';

const Checkout = ({ booking, onGoBack, onConfirm, onNavigate }) => {
  const [guestData, setGuestData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    nationality: '',
    specialRequests: ''
  });

  // Default payment data for Amadeus booking (using test card)
  const [paymentData] = useState({
    method: 'card',
    cardholderName: '',
    cardNumber: '4111111111111111', // Test card
    expiryDate: '12/25', // Test expiry
    cvv: '123' // Test CVV
  });

  const [errors, setErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);

  // Default booking data if not provided
  const defaultBooking = {
    hotelName: 'Grand Plaza Hotel',
    location: 'Paris, France',
    checkIn: new Date(Date.now() + 86400000).toISOString().split('T')[0],
    checkOut: new Date(Date.now() + 172800000).toISOString().split('T')[0],
    nights: 1,
    roomType: 'Deluxe Room',
    guests: 2,
    roomPrice: 299,
    taxes: 0,
    serviceFee: 0
  };

  const currentBooking = booking || defaultBooking;
  
  // Calculate total if not provided
  const calculateTotal = () => {
    if (currentBooking.total) return currentBooking.total;
    const nights = currentBooking.nights || 1;
    const roomPrice = currentBooking.roomPrice || 299;
    const subtotal = roomPrice * nights;
    const taxes = currentBooking.taxes || subtotal * 0.12;
    const serviceFee = currentBooking.serviceFee || subtotal * 0.05;
    return subtotal + taxes + serviceFee;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!guestData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!guestData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!guestData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!guestData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirmBooking = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsProcessing(true);

    try {
      // Calculate total
      const total = calculateTotal();
      
      // Prepare booking data for Amadeus API
      // Use guest name for cardholder name if not provided
      const paymentDataWithName = {
        ...paymentData,
        cardholderName: paymentData.cardholderName || `${guestData.firstName} ${guestData.lastName}`
      };
      
      const bookingPayload = {
        hotelId: currentBooking.hotelId,
        offerId: currentBooking.offerId,
        checkIn: currentBooking.checkIn,
        checkOut: currentBooking.checkOut,
        guests: currentBooking.guests || 2,
        guestData: guestData,
        paymentData: paymentDataWithName
      };

      // Amadeus API removed - use demo booking
      console.log('⚠️ Amadeus API removed - using demo booking');
      if (onConfirm) {
        onConfirm({
          ...currentBooking,
          total,
          guestData,
          paymentData,
          confirmationNumber: Math.random().toString(36).substr(2, 9).toUpperCase(),
          isRealBooking: false
        });
      }
    } catch (error) {
      console.error('❌ Error creating booking:', error);
      alert(`Error: ${error.message || 'Failed to create booking. Please try again.'}`);
      setIsProcessing(false);
      return; // Don't proceed on error
    }
  };


  return (
    <div className="checkout-page">
      <Header onLogoClick={onGoBack} onNavigate={onNavigate} />
      <main className="checkout-main">
        <div className="checkout-container">
          {/* Progress Indicator */}
          <div className="checkout__progress">
            <div className="checkout__progress-step checkout__progress-step--active">
              <span className="checkout__progress-number">1</span>
              <span className="checkout__progress-label">Details</span>
            </div>
            <div className="checkout__progress-line"></div>
            <div className="checkout__progress-step checkout__progress-step--active">
              <span className="checkout__progress-number">2</span>
              <span className="checkout__progress-label">Confirm</span>
            </div>
          </div>

          <div className="checkout__content">
            <div className="checkout__form-section">
              <button className="checkout__back" onClick={onGoBack}>
                ← Back
              </button>

              <h1 className="checkout__title">Complete Your Booking</h1>
              <p className="checkout__subtitle">
                Please provide your information to complete your reservation
              </p>

              <form onSubmit={handleConfirmBooking} className="checkout__form">
                <GuestDetailsForm
                  formData={guestData}
                  onChange={setGuestData}
                  errors={errors}
                />

                <div className="checkout__actions">
                  <Button
                    type="submit"
                    variant="cta"
                    size="lg"
                    disabled={isProcessing}
                    className="checkout__confirm-button"
                  >
                    {isProcessing ? 'Processing...' : 'Confirm Booking'}
                  </Button>
                  <p className="checkout__security-note">
                    🔒 Secure booking • Free cancellation up to 24 hours before check-in
                  </p>
                </div>
              </form>
            </div>

            <div className="checkout__summary-section">
              <BookingSummary booking={currentBooking} />
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Checkout;
